import React, { useState } from 'react';
import * as S from './Input.styles';
import { RequiredStar } from '@/components/UI/RequiredStar/RequiredStar';
import { HelperText } from '@/components/UI/HelperText/HelperText';
import { InputProps } from '@/components/UI/Input/Input.types';
import { useCombinedRefs } from '@/hooks/useCombinedRefs';
import {
  getFinalDecimalValue,
  getFinalValue,
  hasSeparator,
} from '@/components/UI/Input/Input.helpers';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { SearchIcon } from '@/components/UI/Icons/Icons';
import useScreenSize from '@/hooks/useScreenSize';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      appearance,
      clear,
      label,
      helperText,
      hasError,
      hasBorderBottom = true,
      isBigText,
      isNumber,
      isNumberWithSpaces,
      precision = 0,
      customPaddingTop,
      customPaddingBottom,
      leftIcon,
      maxLength,
      ...props
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const combinedRef = useCombinedRefs<HTMLInputElement | null>(ref, inputRef);
    const [isFocused, setIsFocused] = useState(false);
    const { isLarge } = useScreenSize();

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      props.onFocus?.(e);
      setIsFocused(true);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      props.onBlur?.(e);
      setIsFocused(false);
    };

    const onChange = (
      e: React.ChangeEvent<HTMLInputElement> & { data?: string }
    ) => {
      const { value, selectionStart } = e.target;
      const isTel = props.type === 'tel';
      const valueData = {
        value,
        isTel,
        isNumberWithSpaces,
        isNumber,
        maxLength,
      };
      let finalValue = value;

      if (precision) {
        if (e.data && hasSeparator(e.data) && hasSeparator(value)) {
          e.target.value = value.slice(0, -1);
          return false;
        }

        finalValue = getFinalDecimalValue({
          ...valueData,
          precision,
        });
      } else {
        finalValue = getFinalValue(valueData);
      }

      props.onChange?.(finalValue);

      if (combinedRef.current) {
        combinedRef.current.value = finalValue;
      }

      if (isTel || isNumberWithSpaces) {
        const adjustedCursorPosition =
          (selectionStart ?? 0) + finalValue.length - value.length;

        setTimeout(() => {
          combinedRef.current?.setSelectionRange(
            adjustedCursorPosition,
            adjustedCursorPosition
          );
        }, 0);
      }
    };

    const placeholder = label
      ? isFocused
        ? props.placeholder
        : ''
      : props.placeholder;

    if (clear && !appearance) {
      return (
        <S.InputWrapper>
          <S.InputBox
            data-testid={CommonTestIds.input.input}
            ref={combinedRef}
            {...props}
            placeholder={placeholder}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            $appearance={appearance}
            $withClear={!!clear}
            $hasBorderBottom={hasBorderBottom || !!helperText}
            $isBigText={isBigText}
            $customPaddingTop={customPaddingTop}
          />
          {!!props.value && (
            <S.InputClear
              onPointerDown={e => e.preventDefault()}
              onClick={clear}
              $customPaddingTop={customPaddingTop}
            />
          )}
        </S.InputWrapper>
      );
    }

    if (appearance === 'filled') {
      return (
        <S.InputWrapper>
          <S.InputFilledWrapper $isFocused={isFocused} $hasError={hasError}>
            {label && (
              <S.InputFilledLabel
                as="label"
                $variant="3"
                htmlFor={props.name}
                $isShifted={!!props.value || isFocused}
                $hasError={hasError}
              >
                {label} {props.required && <RequiredStar />}
              </S.InputFilledLabel>
            )}
            <S.InputBox
              data-testid={CommonTestIds.input.input}
              ref={combinedRef}
              {...props}
              placeholder={placeholder}
              onChange={onChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              $appearance={appearance}
              $hasBorderBottom={false}
              $isShifted={!!props.value || isFocused}
            />
          </S.InputFilledWrapper>
          {helperText && (
            <HelperText
              helperText={helperText}
              hasError={hasError}
              hasBottomMargin={false}
            />
          )}
        </S.InputWrapper>
      );
    }

    return (
      <S.InputWrapper>
        {appearance === 'filledWithIcon' && (
          <S.InputFilledBg
            $customPaddingTop={customPaddingTop}
            $customPaddingBottom={customPaddingBottom}
          />
        )}

        {appearance === 'outlined' && <S.InputOutlinedBg />}

        {(appearance === 'filledWithIcon' || !!leftIcon) && (
          <S.SearchLeftIconWrapper $appearance={appearance}>
            {leftIcon || <SearchIcon />}
          </S.SearchLeftIconWrapper>
        )}

        {label && (
          <S.InputLabel
            as="label"
            $variant="3"
            htmlFor={props.name}
            $isShifted={!!props.value || isFocused}
          >
            {label} {props.required && <RequiredStar />}
          </S.InputLabel>
        )}

        <S.InputBox
          data-testid={CommonTestIds.input.input}
          ref={combinedRef}
          {...props}
          placeholder={placeholder}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          $appearance={appearance}
          $hasBorderBottom={hasBorderBottom || !!helperText}
          $isBigText={isBigText}
          $customPaddingTop={customPaddingTop}
          $customPaddingBottom={customPaddingBottom}
        />

        {(appearance === 'filledWithIcon' || clear) && !!props.value && (
          <S.InputClear
            onPointerDown={e => e.preventDefault()}
            onClick={clear}
            $hasLeftPadding
            $customPaddingTop={customPaddingTop}
            {...(appearance === 'outlined' && {
              $isYCentered: true,
              $customPaddingRight: isLarge ? 20 : 15,
            })}
          />
        )}

        {helperText && (
          <HelperText helperText={helperText} hasError={hasError} />
        )}
      </S.InputWrapper>
    );
  }
);

Input.displayName = 'Input';
